import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { getAgencyProfile, upsertAgencyProfile } from '../api/endpoints';
import { REQUIRED_AGENCY_PROFILE_FIELD_KEYS } from '../api/types';
import type { AgencyProfile, UpsertAgencyProfileInput } from '../api/types';
import { isApiError } from '../api/client';
import { ErrorState, LoadingState } from '../components/states';

/**
 * The six Task 299 required fields — the SAME constant the operator's
 * detail panel scores against, so the agency's "6 of 6" and the
 * operator's can never mean different things. Re-exported under the name
 * this page has always used because that name is what its own test and
 * callers import; the list itself lives in one place.
 */
export const REQUIRED_PROFILE_FIELDS = REQUIRED_AGENCY_PROFILE_FIELD_KEYS;

type FormState = Record<keyof UpsertAgencyProfileInput, string>;

const EMPTY_FORM: FormState = {
  legal_name: '',
  country_code: '',
  city: '',
  phone_e164: '',
  commercial_registration_no: '',
  vat_no: '',
  billing_contact_name: '',
  billing_contact_email: '',
};

export function completedProfileFieldCount(form: FormState): number {
  return REQUIRED_PROFILE_FIELDS.filter((field) => form[field].trim().length > 0).length;
}

function profileToForm(profile: AgencyProfile | null): FormState {
  if (!profile) return EMPTY_FORM;
  return {
    legal_name: profile.legal_name ?? '',
    country_code: profile.country_code ?? '',
    city: profile.city ?? '',
    phone_e164: profile.phone_e164 ?? '',
    commercial_registration_no: profile.commercial_registration_no ?? '',
    vat_no: profile.vat_no ?? '',
    billing_contact_name: profile.billing_contact_name ?? '',
    billing_contact_email: profile.billing_contact_email ?? '',
  };
}

export function AgencyOnboardingPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [organization, setOrganization] = useState<{
    id: string;
    name: string;
    slug: string;
  } | null>(null);
  const [profile, setProfile] = useState<AgencyProfile | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const completedCount = useMemo(() => completedProfileFieldCount(form), [form]);
  const complete = completedCount === REQUIRED_PROFILE_FIELDS.length;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getAgencyProfile({ signal: controller.signal })
      .then((data) => {
        setOrganization(data.organization);
        setProfile(data.profile);
        setForm(profileToForm(data.profile));
        setCanEdit(data.can_edit);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(describeError(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  function update(field: keyof FormState, value: string) {
    setSaved(false);
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canEdit || saving) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const data = await upsertAgencyProfile(form);
      setProfile(data.profile);
      setForm(profileToForm(data.profile));
      setSaved(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingState title="جاري تحميل ملف الوكالة…" />;
  }
  if (error && !organization) {
    return <ErrorState title="تعذّر تحميل ملف الوكالة" description={error} />;
  }

  return (
    <section className="stack-md" data-testid="agency-onboarding-page">
      <div className="page-head">
        <div>
          <span className="pill">إعداد الوكالة</span>
          <h1 className="h-page" style={{ marginTop: 10 }}>
            المعلومات القانونية والتواصل
          </h1>
          <p className="sub">
            أكمل بيانات الوكالة ليتمكن فريق المنصة من مراجعتها. حفظ هذه البيانات لا يفعّل الحساب
            تلقائياً.
          </p>
        </div>
        <div style={{ minWidth: 180, textAlign: 'end' }}>
          <span className={profile?.completed_at ? 'pill pill-success' : 'pill'}>
            {profile?.completed_at ? 'الملف مكتمل' : `${completedCount} / 6 حقول مطلوبة`}
          </span>
        </div>
      </div>

      <section className="card" style={{ padding: 20 }}>
        <div className="sec-title">
          <h2>هوية الوكالة</h2>
          <span className="meta">للقراءة فقط</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
            gap: 14,
          }}
        >
          <ReadOnlyField label="اسم الوكالة" value={organization?.name} />
          <ReadOnlyField label="المعرّف الداخلي" value={organization?.slug} ltr />
        </div>
      </section>

      <form className="card" style={{ padding: 20 }} onSubmit={onSubmit} noValidate>
        <div className="sec-title">
          <div>
            <h2>ملف الوكالة</h2>
            <p className="tiny" style={{ marginTop: 5 }}>
              السجل التجاري والرقم الضريبي اختياريان في هذه المرحلة.
            </p>
          </div>
          {!canEdit ? <span className="pill">قراءة فقط</span> : null}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
            gap: 16,
          }}
        >
          <ProfileInput
            id="agency-legal-name"
            label="الاسم القانوني"
            value={form.legal_name}
            onChange={(value) => update('legal_name', value)}
            disabled={!canEdit}
            required
            autoComplete="organization"
          />
          <ProfileInput
            id="agency-country-code"
            label="رمز الدولة"
            value={form.country_code}
            onChange={(value) => update('country_code', value)}
            disabled={!canEdit}
            required
            ltr
            placeholder="SA"
            maxLength={2}
          />
          <ProfileInput
            id="agency-city"
            label="المدينة"
            value={form.city}
            onChange={(value) => update('city', value)}
            disabled={!canEdit}
            required
            autoComplete="address-level2"
          />
          <ProfileInput
            id="agency-phone"
            label="رقم الهاتف"
            value={form.phone_e164}
            onChange={(value) => update('phone_e164', value)}
            disabled={!canEdit}
            required
            ltr
            placeholder="+9665XXXXXXXX"
            autoComplete="tel"
          />
          <ProfileInput
            id="agency-cr"
            label="رقم السجل التجاري (اختياري)"
            value={form.commercial_registration_no}
            onChange={(value) => update('commercial_registration_no', value)}
            disabled={!canEdit}
            ltr
          />
          <ProfileInput
            id="agency-vat"
            label="الرقم الضريبي (اختياري)"
            value={form.vat_no}
            onChange={(value) => update('vat_no', value)}
            disabled={!canEdit}
            ltr
          />
          <ProfileInput
            id="agency-billing-name"
            label="اسم جهة التواصل"
            value={form.billing_contact_name}
            onChange={(value) => update('billing_contact_name', value)}
            disabled={!canEdit}
            required
            autoComplete="name"
          />
          <ProfileInput
            id="agency-billing-email"
            label="بريد جهة التواصل"
            value={form.billing_contact_email}
            onChange={(value) => update('billing_contact_email', value)}
            disabled={!canEdit}
            required
            ltr
            type="email"
            autoComplete="email"
          />
        </div>

        {error ? (
          <div className="banner banner-error" role="alert" style={{ marginTop: 16 }}>
            {error}
          </div>
        ) : null}
        {saved ? (
          <div className="banner banner-success" role="status" style={{ marginTop: 16 }}>
            تم حفظ بيانات الوكالة.
          </div>
        ) : null}

        {canEdit ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 18,
              flexWrap: 'wrap',
            }}
          >
            <p className="tiny">
              {complete
                ? 'البيانات المطلوبة مكتملة وجاهزة للمراجعة اليدوية.'
                : 'يمكنك الحفظ الآن والعودة لإكمال الحقول المطلوبة لاحقاً.'}
            </p>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'جاري الحفظ…' : 'حفظ بيانات الوكالة'}
            </button>
          </div>
        ) : (
          <p className="tiny" style={{ marginTop: 18 }}>
            يمكن لمالك الوكالة فقط تعديل هذه البيانات.
          </p>
        )}
      </form>
    </section>
  );
}

function ProfileInput({
  id,
  label,
  value,
  onChange,
  disabled,
  required = false,
  ltr = false,
  type = 'text',
  placeholder,
  maxLength,
  autoComplete,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
  readonly required?: boolean;
  readonly ltr?: boolean;
  readonly type?: string;
  readonly placeholder?: string;
  readonly maxLength?: number;
  readonly autoComplete?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label} {required ? <span aria-hidden="true">*</span> : null}
      </label>
      <input
        id={id}
        name={id}
        value={value}
        type={type}
        dir={ltr ? 'ltr' : undefined}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-required={required}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  ltr = false,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
  readonly ltr?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span className="lbl">{label}</span>
      <span className="body" dir={ltr ? 'ltr' : undefined}>
        {value || '—'}
      </span>
    </div>
  );
}

function describeError(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === 'validation_error') return 'راجع الحقول المدخلة ثم حاول مجدداً.';
    if (err.code === 'permission_denied') return 'لا تملك صلاحية تعديل ملف الوكالة.';
    if (err.code === 'commercial_activation_required') {
      return 'ملف الوكالة غير متاح في حالة الحساب الحالية.';
    }
  }
  return 'تعذّر إكمال الطلب. حاول مجدداً.';
}
